import { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";

const BILLING_URL = "https://paylora-billing.vercel.app";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        // Set the user right away so navigation isn't blocked on a network
        // round-trip - then refresh in the background afterward to pick up
        // anything that changed elsewhere (like email verification status),
        // updating again only if something actually did.
        setUser(Object.assign(Object.create(Object.getPrototypeOf(u)), u));
        setLoading(false);
        try {
          await u.reload();
          setUser(Object.assign(Object.create(Object.getPrototypeOf(u)), u));
        } catch (err) {
          // If reload fails (e.g. a transient network issue right after a
          // redirect back from Stripe checkout), don't let that break
          // anything - we already have a working user object.
          console.error("Auth reload failed, continuing with existing session:", err);
        }
        return;
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const login = (email, password) => signInWithEmailAndPassword(auth, email, password);

  async function signup(email, password, { fullName, companyName }) {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName: fullName });
    try {
      await fetch(`${BILLING_URL}/api/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "email_verification", to: cred.user.email }),
      });
    } catch (err) {
      console.error("Verification email error:", err);
    }
    await cred.user.reload();
    const trialStartedAt = new Date().toISOString();
    const trialExpiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
    await setDoc(
      doc(db, "users", cred.user.uid, "company", "profile"),
      {
        name: companyName,
        email: email,
        logoUrl: "",
        phone: "",
        address: "",
        website: "",
        accentColor: "#4F46E5",
        invoiceFooterNote: "Thank you for your business!",
        taxId: "",
      },
      { merge: true }
    );
    await setDoc(
      doc(db, "users", cred.user.uid),
      {
        fullName,
        email,
        trialStartedAt,
        trialExpiresAt,
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );

    // Apply referral if exists
    const referralCode = sessionStorage.getItem("referralCode");
    if (referralCode) {
      const { applyReferral } = await import("../utils/referrals");
      await applyReferral(cred.user.uid, referralCode);
      sessionStorage.removeItem("referralCode");
    }

    return cred;
  }

  const logout = () => {
    sessionStorage.removeItem("wasLoggedIn");
    return signOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
